import React, { useState, useRef, useCallback } from "react"
import cn from "classnames"
import { useToast } from "~/utils/Toast"
import request from "~/lib/request"
import { Upload, X, FileText, Play } from "lucide-react"
import { Image as ImageIcon } from "~/library/UIkit/Icons"
import styles from "./FileUploader.module.scss"

export interface UploadedFile {
  id: string
  name: string
  url: string
  type: string
  size: number
  mimeType: string
}

interface FileUploadProgress {
  file: File
  progress: number
  error?: string
  uploaded?: UploadedFile
}

export interface FileUploaderProps {
  path: string
  formDataKey?: string
  onUpload?: (files: UploadedFile[]) => void
  onFileAdded?: (file: File) => void
  onFileRemoved?: (fileId: string) => void
  multiple?: boolean
  accept?: string
  maxSize?: number // in MB
  maxFiles?: number
  existingFiles?: UploadedFile[]
  disabled?: boolean
  className?: string
}

const FileUploader: React.FC<FileUploaderProps> = ({
  path,
  formDataKey = "media",
  onUpload,
  onFileAdded,
  onFileRemoved,
  multiple = true,
  accept = "image/*,video/*,.pdf",
  maxSize = 10, // 10MB default
  maxFiles = 5,
  existingFiles = [],
  disabled = false,
  className = "",
}) => {
  const [uploadQueue, setUploadQueue] = useState<FileUploadProgress[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(existingFiles)
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set())
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return `File size must be less than ${maxSize}MB`
      }

      // Check file type
      const allowedTypes = accept.split(",").map((type) => type.trim())
      const isValidType = allowedTypes.some((type) => {
        if (type.startsWith(".")) {
          return file.name.toLowerCase().endsWith(type.toLowerCase())
        }
        return file.type.match(type.replace("*", ".*"))
      })

      if (!isValidType) {
        return "File type not supported"
      }

      return null
    },
    [accept, maxSize]
  )

  const uploadFile = useCallback(
    async (fileProgress: FileUploadProgress): Promise<UploadedFile> => {
      const formData = new FormData()
      formData.append(`${formDataKey}[file]`, fileProgress.file)
      formData.append(`${formDataKey}[title]`, fileProgress.file.name)
      formData.append(`${formDataKey}[alt]`, fileProgress.file.name)

      const response = await request({ path, method: "POST", data: formData, asFormData: true })

      return {
        id: response.id,
        name: fileProgress.file.name,
        url: response.url,
        type: getFileType(fileProgress.file),
        size: fileProgress.file.size,
        mimeType: fileProgress.file.type,
      }
    },
    [path]
  )

  const getFileType = (file: File): string => {
    if (file.type.startsWith("image/")) return "image"
    if (file.type.startsWith("video/")) return "video"
    if (file.type === "application/pdf") return "pdf"
    return "other"
  }

  const processFiles = useCallback(
    async (files: File[]) => {
      if (disabled) return

      // Check max files limit
      const totalFiles = uploadedFiles.length + uploadQueue.length + files.length
      if (totalFiles > maxFiles) {
        toast(`Maximum ${maxFiles} files allowed`, "danger")
        return
      }

      const validFiles: File[] = []
      const invalidFiles: string[] = []

      files.forEach((file) => {
        const error = validateFile(file)
        if (error) {
          invalidFiles.push(`${file.name}: ${error}`)
        } else {
          validFiles.push(file)
        }
      })

      if (invalidFiles.length > 0) {
        toast(`Invalid files: ${invalidFiles.join(", ")}`, "danger")
      }

      if (validFiles.length === 0) return

      // Add files to upload queue
      const newUploads: FileUploadProgress[] = validFiles.map((file) => ({
        file,
        progress: 0,
      }))

      setUploadQueue((prev) => [...prev, ...newUploads])

      // Call onFileAdded for each valid file
      validFiles.forEach((file) => onFileAdded?.(file))

      // Start uploading files
      const uploadPromises = newUploads.map(async (fileProgress) => {
        try {
          // Simulate progress (since we don't have real progress from the API)
          const progressInterval = setInterval(() => {
            setUploadQueue((prev) =>
              prev.map((item) =>
                item.file === fileProgress.file && item.progress < 90
                  ? { ...item, progress: item.progress + 10 }
                  : item
              )
            )
          }, 200)

          const uploadedFile = await uploadFile(fileProgress)

          clearInterval(progressInterval)

          // Update queue with completed upload
          setUploadQueue((prev) =>
            prev.map((item) =>
              item.file === fileProgress.file
                ? { ...item, progress: 100, uploaded: uploadedFile }
                : item
            )
          )

          // Add to uploaded files
          setUploadedFiles((prev) => [...prev, uploadedFile])

          return uploadedFile
        } catch (error: any) {
          const errorMessage = error.errors?.join(", ") || "Upload failed"
          setUploadQueue((prev) =>
            prev.map((item) =>
              item.file === fileProgress.file ? { ...item, error: errorMessage } : item
            )
          )
          toast(`Failed to upload ${fileProgress.file.name}: ${errorMessage}`, "danger")
          throw error
        }
      })

      try {
        const results = await Promise.allSettled(uploadPromises)
        const successfulUploads = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => (result as PromiseFulfilledResult<UploadedFile>).value)

        if (successfulUploads.length > 0) {
          onUpload?.(successfulUploads)
          toast(`${successfulUploads.length} file(s) uploaded successfully`, "success")
        }

        // Remove completed uploads from queue after a delay
        setTimeout(() => {
          setUploadQueue((prev) => prev.filter((item) => !item.uploaded && !item.error))
        }, 2000)
      } catch (error) {
        console.error("Upload error:", error)
      }
    },
    [
      disabled,
      maxFiles,
      uploadedFiles.length,
      uploadQueue.length,
      validateFile,
      onFileAdded,
      onUpload,
      toast,
      uploadFile,
    ]
  )

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      const files = Array.from(e.dataTransfer.files)
      if (!multiple && files.length > 1) {
        toast("Only one file allowed", "danger")
        return
      }

      processFiles(files)
    },
    [multiple, processFiles, toast]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      if (files.length > 0) {
        processFiles(files)
      }
      // Reset input value
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    },
    [processFiles]
  )

  const deleteFile = useCallback(
    async (fileId: string) => {
      try {
        const deletePath = `${path}/${fileId}`
        await request({ path: deletePath, method: "DELETE", data: {}, asFormData: false })
        return true
      } catch (error: any) {
        const errorMessage = error.errors?.join(", ") || "Failed to delete file"
        toast(`Error deleting file: ${errorMessage}`, "danger")
        return false
      }
    },
    [path, toast]
  )

  const removeFile = useCallback(
    async (fileId: string) => {
      if (deletingFiles.has(fileId)) return // Prevent multiple delete attempts

      setDeletingFiles((prev) => new Set(prev).add(fileId))

      try {
        const deleted = await deleteFile(fileId)
        if (deleted) {
          setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId))
          onFileRemoved?.(fileId)
          toast("File deleted successfully", "success")
        }
      } finally {
        setDeletingFiles((prev) => {
          const newSet = new Set(prev)
          newSet.delete(fileId)
          return newSet
        })
      }
    },
    [deleteFile, deletingFiles, onFileRemoved, toast]
  )

  const removeUploadingFile = useCallback((file: File) => {
    setUploadQueue((prev) => prev.filter((item) => item.file !== file))
  }, [])

  const renderFilePreview = (file: UploadedFile) => {
    switch (file.type) {
      case "image":
        return (
          <div key={file.id} className={cn(styles.filePreview, styles.imagePreview)}>
            <div className={styles.imageHeader}>
              <ImageIcon width={24} height={24} className={styles.imageIcon} />
              <div className={styles.imageInfo}>
                <div className={styles.imageName}>{file.name}</div>
                <div className={styles.imageSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button
                type="button"
                className={cn(
                  styles.button,
                  styles.small,
                  styles.danger,
                  styles.removeButton,
                  styles.absolute
                )}
                onClick={() => removeFile(file.id)}
                disabled={deletingFiles.has(file.id)}
              >
                {deletingFiles.has(file.id) ? "..." : <X size={12} />}
              </button>
            </div>
            <img src={file.url} alt={file.name} className={styles.previewImage} />
          </div>
        )

      case "video":
        return (
          <div key={file.id} className={cn(styles.filePreview, styles.videoPreview)}>
            <div className={styles.videoHeader}>
              <Play size={24} className={styles.videoIcon} />
              <div className={styles.videoInfo}>
                <div className={styles.videoName}>{file.name}</div>
                <div className={styles.videoSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button
                type="button"
                className={cn(
                  styles.button,
                  styles.small,
                  styles.danger,
                  styles.removeButton,
                  styles.absolute
                )}
                onClick={() => removeFile(file.id)}
                disabled={deletingFiles.has(file.id)}
              >
                {deletingFiles.has(file.id) ? "..." : <X size={12} />}
              </button>
            </div>
            <video controls src={file.url} className={styles.videoElement}>
              Your browser does not support the video tag.
            </video>
          </div>
        )

      case "pdf":
        return (
          <div key={file.id} className={cn(styles.filePreview, styles.pdfPreview)}>
            <div className={styles.pdfHeader}>
              <FileText size={24} className={styles.pdfIcon} />
              <div className={styles.pdfInfo}>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.pdfLink}
                >
                  {file.name}
                </a>
                <div className={styles.pdfSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button
                type="button"
                className={cn(
                  styles.button,
                  styles.small,
                  styles.danger,
                  styles.removeButton,
                  styles.absolute
                )}
                onClick={() => removeFile(file.id)}
                disabled={deletingFiles.has(file.id)}
              >
                {deletingFiles.has(file.id) ? "..." : <X size={12} />}
              </button>
            </div>
          </div>
        )

      default:
        return (
          <div key={file.id} className={cn(styles.filePreview, styles.genericFilePreview)}>
            <div className={styles.fileHeader}>
              <FileText size={24} className={styles.fileIcon} />
              <div className={styles.fileInfo}>
                <div className={styles.genericFileName}>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.pdfLink}
                  >
                    {file.name}
                  </a>
                </div>
                <div className={styles.genericFileSize}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
              <button
                type="button"
                className={cn(styles.button, styles.small, styles.danger, styles.removeButton)}
                onClick={() => removeFile(file.id)}
                disabled={deletingFiles.has(file.id)}
              >
                {deletingFiles.has(file.id) ? "..." : <X size={12} />}
              </button>
            </div>
          </div>
        )
    }
  }

  const renderUploadProgress = (progress: FileUploadProgress) => {
    return (
      <div key={progress.file.name} className={styles.progressItem}>
        <div className={styles.progressHeader}>
          <div className={styles.progressInfo}>
            <div className={styles.fileName}>{progress.file.name}</div>
            <div className={styles.fileSize}>
              {(progress.file.size / 1024 / 1024).toFixed(2)} MB
            </div>
          </div>
          <button
            type="button"
            className={cn(styles.button, styles.small, styles.outline)}
            style={{ width: "24px", height: "24px" }}
            onClick={() => removeUploadingFile(progress.file)}
          >
            <X size={12} />
          </button>
        </div>
        {progress.error ? (
          <div className={styles.errorText}>{progress.error}</div>
        ) : (
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              role="progressbar"
              style={{ width: `${progress.progress}%` }}
              aria-valuenow={progress.progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn(className)}>
      {/* Upload Zone */}
      <div
        className={cn(
          styles.uploadZone,
          { [styles.dragOver]: isDragOver },
          { [styles.disabled]: disabled }
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Upload size={32} className={styles.uploadIcon} />
        <p className={styles.uploadText}>
          {isDragOver ? "Drop files here" : "Click to select files or drag and drop"}
        </p>
        <div className={styles.uploadHint}>
          Supports images, videos, and PDFs up to {maxSize}MB
          {multiple && ` (max ${maxFiles} files)`}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className={styles.hiddenInput}
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        disabled={disabled}
      />

      {/* Upload Progress */}
      {uploadQueue.length > 0 && (
        <div className={styles.progressSection}>
          <div className={styles.progressTitle}>Uploading...</div>
          {uploadQueue.map((progress) => renderUploadProgress(progress))}
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className={styles.uploadedSection}>
          <div className={styles.uploadedTitle}>Uploaded Files</div>
          <div className={styles.filesGrid}>
            {uploadedFiles.map((file) => renderFilePreview(file))}
          </div>
        </div>
      )}
    </div>
  )
}

export default FileUploader
